import { DateTime } from '@mode-switch/express';
import {
  AvatarTypes,
  DataViewResponseTypes,
  DataViewTypes,
  DatasetBrowserData,
  ModeHalResource,
  SwitchViewDomain,
  SwitchViewResponseTypes,
} from '@mode/shared/contract-common';
import {
  DatasetDetails,
  ProducerTypes,
  ThoughtspotPublication,
  TsPublishMetaData,
} from 'libs/shared/contract-common/src/lib/dataset';
import { QueryResource } from '../queries/query-response.types';
import { ReportResource } from '../reports/report-response.types';
import { toSwitchAttr } from './switch-view-data-mappers';

/**
 * Create switchViewFeature from the BE switch_view response.
 */
export function mapToSwitchView(
  response: DataViewResponseTypes.NormalizedSwitchViewResponse
): SwitchViewDomain.SwitchView {
  const attrResources = response._embedded.switch_attrs._embedded.switch_attrs;
  let switchAttrs: SwitchViewDomain.SwitchAttr[] = [];
  if (attrResources != null) {
    switchAttrs = attrResources.map((r: SwitchViewResponseTypes.SwitchViewAttrResource) =>
      toSwitchAttr(r as SwitchViewResponseTypes.SwitchViewAttrResource)
    );
  }

  return {
    token: response.token,
    createdAt: response.created_at,
    flamingoSignature: response.flamingo_signature,
    updatedAt: response.updated_at,
    sourceToken: response.source_token,
    sourceType: response.source_type,
    switchAttrs: switchAttrs,
    orphan: response.orphan,
    rootModelIsSample: response.root_model_is_sample,
    rootSourceExists: response.root_source_exists,
    rootSourceModelName: response.model_name ?? '',
    rootSourceModelType: response.model_type ?? '',
    isDataRefreshAvailable: response.data_refresh_available ?? false,
    dataRefreshAvailableAsOf: response.data_refresh_available_as_of,
    isFallbackData: response.isFallbackData,
    producerType: response.producer_type ?? null,
    producerToken: response.producer_token,
    weekStart: DateTime.weekStart(response.week_start),
    yearStart: DateTime.yearStart(response.year_start),
    error: false,
    rowOrder: response.row_order ?? null,
  };
}

/**
 * Map switchView response to DataView. This data mappers assumes the switch view has a dataset embed.
 *
 * TODO: Along with the api deprecation, data mappers will need to be adjusted to take each resource as
 *   an arguement.
 */
export function switchViewResponseToDataView(
  response: DataViewResponseTypes.NormalizedSwitchViewResponse,
  details?: DatasetDetails
): SwitchViewDomain.DataView {
  return {
    datasetResult: mapToDatasetResult(response),
    thoughtspot: mapToPublishResult(response),
    tsPublishMetaData: mapToPublishMetaData(response),
    switchView: mapToSwitchView(response),
    ...(details ? { details: details } : null),
  };
}

export function mapToDatasetResult(
  response: DataViewResponseTypes.NormalizedSwitchViewResponse
): DataViewTypes.DatasetResult {
  // If the result is not stored (meaning in s3), the BE does not return anything in the `_embedded.result`.
  // This represents a case where a query fails, so nothing is persisted to s3. However, we still need to backfill
  // with an empty result because the current code path depends on values existing for this. In the future, we could
  // change the error path to be handled differently now that it's not coupled with the BE behavior here.
  const runResult: DataViewTypes.RunResultResource = (response._embedded.dataset &&
    response._embedded?.dataset?._embedded?.result) ?? {
    bytes: 0,
    columns: [],
    content_length: 0,
    count: 0,
    created_at: '',
    last_modified: '',
    oversized: false,
    row_count: null,
    state: 'waiting',
    token: '',
    _links: {},
  };

  if (response.orphan) {
    return {
      bytes: runResult.bytes,
      completedAt: '1970-01-01T00:00:00Z',
      contentLength: runResult.content_length,
      createdAt: runResult.created_at,
      csvUrl: runResult._links.csv?.href ?? '',
      datasourceId: null,
      executedBy: 'underlying-dataset-has-been-deleted',
      executedByEmail: 'underlying-dataset-has-been-deleted',
      isFallbackData: response.isFallbackData,
      isPurged: !!response._embedded?.dataset?.purge_started_at,
      jsonUrl: runResult._links.json?.href ?? '',
      metaColumns: runResult.columns,
      legacyContent: [],
      oversized: runResult.oversized,
      queryCreatedAt: '1970-01-01T00:00:00Z',
      queryName:
        response.producer_type === ProducerTypes.NotebookCell
          ? response._embedded.dataset.query_name
          : 'underlying-dataset-has-been-deleted',
      queryRunState: DataViewTypes.QueryRunStatus.Excluded,
      queryRunToken: 'underlying-dataset-has-been-deleted',
      queryToken:
        response.producer_type === ProducerTypes.NotebookCell
          ? response._embedded.dataset.query_token
          : 'underlying-dataset-has-been-deleted',
      rawQuery: 'underlying-dataset-has-been-deleted',
      renderedQuery: 'underlying-dataset-has-been-deleted',
      reportDataUrl: 'underlying-dataset-has-been-deleted',
      reportQueryUrl: 'underlying-dataset-has-been-deleted',
      rowCount: runResult.row_count,
      switchViewToken: response.token,
      datasetToken: response.root_model_token,
      token: runResult.token,
      producerType: response.producer_type ?? null,
      weekStart: DateTime.weekStart(response.week_start),
      yearStart: DateTime.yearStart(response.year_start),
      error: null,
    };
  }

  const props = extractDatasetResultProps({
    runResult,
    producerRun: response._embedded.dataset?._embedded?.producer_run,
    queryRun: response._embedded.dataset,
  });

  return {
    ...props,
    thoughtspot: mapToPublishResult(response),
    isFallbackData: response.isFallbackData,
    switchViewToken: response.token,
    datasetToken: response.root_model_token,
    legacyContent: [],
    producerType: response.producer_type ?? null,
    producerToken: response.producer_token,
    weekStart: DateTime.weekStart(response.week_start),
    yearStart: DateTime.yearStart(response.year_start),
    error: null,
  };
}

export function mapToPublishResult(
  response: DataViewResponseTypes.NormalizedSwitchViewResponse
): ThoughtspotPublication | undefined {
  if (response.publish?.thoughtspot_published_at) {
    return {
      publishedAt: response.publish?.thoughtspot_published_at,
      publisher: response.publish?._embedded?.thoughtspot_publisher?.name || '',
      publisherUsername: response.publish?._embedded?.thoughtspot_publisher?.username || '',
      datasetThoughtspotPublicationToken: response.publish.dataset_thoughtspot_publication_token,
    };
  }
  return;
}

export function mapToPublishMetaData(
  response: DataViewResponseTypes.NormalizedSwitchViewResponse
): TsPublishMetaData | undefined {
  if (response?.tsPublishMetaData) {
    return {
      schemaName: response?.tsPublishMetaData?.schema_name ?? '',
      tableName: response?.tsPublishMetaData?.table_name ?? '',
    };
  }
  return;
}

export function extractDatasetResultProps({
  queryRun,
  producerRun,
  runResult,
}: {
  queryRun: DataViewTypes.QueryRunResource;
  producerRun?: DataViewTypes.SqlProducerRunResource | DataViewTypes.GoogleSheetProducerRunResource;
  runResult: DataViewTypes.RunResultResource;
}) {
  let producerResult: DataViewTypes.GoogleSheetResult | undefined;
  let datasourceId = queryRun.data_source_id;
  let rawQuery = queryRun.raw_source;
  let renderedQuery = queryRun.rendered_source;

  if (producerRun) {
    const isGoogleSheetProducer = 'spreadsheet_name' in producerRun;

    if (isGoogleSheetProducer) {
      producerResult = {
        spreadsheetName: producerRun.spreadsheet_name,
        spreadsheetUrl: producerRun.spreadsheet_url,
        worksheetName: producerRun.worksheet_name,
        worksheetRange: producerRun.worksheet_range ?? 'Full Sheet (includes any hidden or filtered cells)',
        ownedBy: producerRun.owned_by,
        connectedBy: producerRun.connected_by,
      };
    } else {
      const { data_source_id, raw_source, rendered_source } = producerRun;
      if (data_source_id) datasourceId = data_source_id;
      if (raw_source) rawQuery = raw_source;
      if (rendered_source) renderedQuery = rendered_source;
    }
  }

  return {
    completedAt: queryRun.completed_at,
    datasourceId,
    dbtMetricToken: queryRun.dbt_metric_token,
    executedBy: queryRun._embedded?.creator.name,
    executedByEmail: queryRun._embedded?.creator.email ?? '',
    isPurged: !!queryRun.purge_started_at,
    queryCreatedAt: queryRun.query_created_at,
    queryName: queryRun.query_name,
    queryRunState: queryRun.state,
    queryRunToken: queryRun.token,
    queryToken: queryRun.query_token,
    rawQuery,
    renderedQuery,
    reportDataUrl: queryRun._links?.result_web.href,
    reportQueryUrl: queryRun._links?.query_web.href,
    bytes: runResult.bytes,
    contentLength: runResult.content_length,
    createdAt: runResult.created_at,
    csvUrl: runResult._links.csv?.href ?? '',
    jsonUrl: runResult._links.json?.href ?? '',
    metaColumns: runResult.columns,
    oversized: runResult.oversized,
    rowCount: runResult.row_count,
    token: runResult.token,
    isMetric: Number.isFinite(queryRun.dbt_metric_id),
    producerResult,
  };
}

/**
 * Helper function to normalize the switchView response.
 *
 * This is to help simplify the stopgap solution where the switchView and dataset embeds have
 * been refactored, but we haven't yet fully updated the HAL pattern in the FE data layer.
 * This should be removed once that adjustment is made.
 */
export function normalizeSwitchView(
  switchViewResponse: DataViewResponseTypes.SwitchViewWithRunsResponses
): DataViewResponseTypes.NormalizedSwitchViewResponse {
  let dataset: DataViewTypes.QueryRunResource;
  let publish!: DataViewTypes.PublishDatasetResource;
  let tsPublishMetaData: DataViewTypes.TsPublishMetaDataResource = {} as DataViewTypes.TsPublishMetaDataResource;
  let isFallbackData = switchViewResponse.last_source_run_failed ?? false;

  if (switchViewResponse._embedded.last_successful_data_run) {
    dataset = switchViewResponse._embedded.last_successful_data_run;
  } else if (switchViewResponse._embedded.last_data_run) {
    dataset = switchViewResponse._embedded.last_data_run;
  } else if (switchViewResponse._embedded.data_run_for_run_in_context) {
    dataset = switchViewResponse._embedded.data_run_for_run_in_context;
  } else {
    dataset = switchViewResponse._embedded.data_run_for_run_in_context ?? ({} as DataViewTypes.QueryRunResource);
  }

  if (switchViewResponse._embedded.query?.thoughtspot_published_at) {
    publish = {
      thoughtspot_published_at: switchViewResponse._embedded.query?.thoughtspot_published_at,
      thoughtspot_publish_url: switchViewResponse._embedded.query?.thoughtspot_publish_url,
      dataset_thoughtspot_publication_token: switchViewResponse._embedded.query?.dataset_thoughtspot_publication_token,
      _embedded: {
        thoughtspot_publisher: switchViewResponse._embedded.query?._embedded?.thoughtspot_publisher,
      },
    };
  }

  if (switchViewResponse._embedded.query?._embedded?.ts_publish_metadata) {
    tsPublishMetaData = {
      schema_name: switchViewResponse._embedded.query?._embedded?.ts_publish_metadata?.schema_name,
      table_name: switchViewResponse._embedded.query?._embedded?.ts_publish_metadata?.table_name,
    };
  }

  return {
    ...switchViewResponse,
    isFallbackData,
    publish,
    tsPublishMetaData,
    _embedded: {
      switch_attrs: switchViewResponse._embedded.switch_attrs,
      dataset,
    },
  };
}

/**
 * Temporarily used to fill a switch view response of an unrun but persisted query so the
 * rest of the flow accommodates appropriately.
 *
 * Optionally, you can choose the key to use if necessary. This is a workaround until the rest of the
 * api decprecation/refactor work can be finished.
 */
export function fillUnrunQuerySwitchViewResource(
  unrunSwitchViewResource: SwitchViewResponseTypes.SwitchViewResource,
  queryResource: QueryResource
): SwitchViewResponseTypes.SwitchViewResource {
  return {
    ...unrunSwitchViewResource,
    _embedded: {
      ...unrunSwitchViewResource._embedded,
      last_data_run: {
        completed_at: '',
        created_at: queryResource['create_at'],
        data_source_id: queryResource.data_source_id,
        id: queryResource.id,
        limit: false,
        max_result_bytes: 0,
        parameters: {},
        query_created_at: '',
        query_name: queryResource.name,
        query_token: queryResource.token,
        raw_source: queryResource.raw_query,
        rendered_source: '',
        state: DataViewTypes.QueryRunStatus.NotYetRun,
        token: '',

        _embedded: {
          creator: {
            username: '',
            token: '',
            name: '',
            avatar: { type: AvatarTypes.AvatarType.Initials, initials: '' },

            _links: {
              self: { href: '' },
            },
          },
        },

        _links: {
          self: { href: '' },
          query_web: { href: '' },
          result_web: { href: '' },
        },
      },
    },
  };
}

/**
 * Map to SwitchView Domain ImportDatasetResult
 *
 */
export function toImportDatasetResult(
  response: DataViewResponseTypes.ImportDatasetResult
): SwitchViewDomain.ImportDatasetResult {
  return {
    switchViewToken: response.switch_view_token,
    queryToken: response.query_token,
    vizToken: response?.viz_token,
  };
}

export function toDataset(response: ReportResource): DatasetBrowserData {
  const localSwitchViews: ModeHalResource = (response._embedded?.['local_switch_views'] ?? {}) as ModeHalResource;
  const switchViews: SwitchViewResponseTypes.SwitchViewResource[] = (localSwitchViews._embedded?.['switch_views'] ??
    []) as SwitchViewResponseTypes.SwitchViewResource[];
  const creator = (response._embedded as any).creator.name;
  const switchAttrs = (switchViews as any)[0]._embedded?.switch_attrs._embedded.switch_attrs.map((attr: any) =>
    toSwitchAttr(attr)
  );
  const lastSuccessfulDataRun = (switchViews as any)[0]._embedded.last_successful_data_run;
  return {
    name: response.name ?? '',
    description: response.description ?? '',
    collection: response?.['collection_name'] ?? '',
    lastRefreshed: response.last_successfully_run_at,
    creator: creator ?? '',
    datasetToken: response.token,
    switchViewToken: switchViews[0].token,
    queryToken: switchViews[0].source_token,
    queryRunToken: lastSuccessfulDataRun.token,
    metaColumns: lastSuccessfulDataRun?._embedded?.result?.columns ?? [],
    switchAttrs: switchAttrs,
  };
}
